import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import { COUNTRIES } from '../utils/constants';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

export default function ProbationPoliciesTab({ user }) {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [editingPolicy, setEditingPolicy] = useState(null);
  const [formData, setFormData] = useState({
    country: '',
    probation_period_months: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/probation_policies`, {
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to fetch policies');
      const data = await res.json();
      setPolicies(data.policies || []);
    } catch (error) {
      console.error('Error fetching policies:', error);
      setPolicies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const url = editingPolicy 
        ? `${API_BASE}/api/admin/probation_policies/${editingPolicy.id}`
        : `${API_BASE}/api/admin/probation_policies`;
      
      const method = editingPolicy ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Email': user?.email || ''
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Failed to save policy');

      await fetchPolicies();
      setShowPolicyModal(false);
      setFormData({ country: '', probation_period_months: '', description: '' });
      setEditingPolicy(null);
    } catch (error) {
      console.error('Error saving policy:', error);
      alert('Error saving policy: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (policy) => {
    setEditingPolicy(policy);
    setFormData({
      country: policy.country,
      probation_period_months: policy.probation_period_months.toString(),
      description: policy.description || ''
    });
    setShowPolicyModal(true);
  };

  const handleDelete = async (policyId) => {
    if (!window.confirm('Are you sure you want to delete this probation policy?')) {
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/api/admin/probation_policies/${policyId}`, {
        method: 'DELETE',
        headers: {
          'X-User-Email': user?.email || ''
        },
        credentials: 'include'
      });

      if (!res.ok) throw new Error('Failed to delete policy');

      await fetchPolicies();
    } catch (error) {
      console.error('Error deleting policy:', error);
      alert('Error deleting policy: ' + error.message);
    }
  };

  const getCountryLabel = (countryCode) => {
    const country = COUNTRIES.find(c => c.value === countryCode);
    return country ? country.label : countryCode;
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        fontSize: '16px',
        color: 'var(--symbolica-muted)'
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '12px' 
        }}>
          <div style={{
            width: '24px',
            height: '24px',
            border: '2px solid #e5e7eb',
            borderTop: '2px solid var(--symbolica-black)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          Loading probation policies...
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ marginBottom: '2rem' }}>
        <h2 className="content-title" style={{ marginBottom: '0.5rem' }}>
          Probation Policies
        </h2>
        <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px' }}>
          Manage probation policies by country. These policies determine the probation period for employees in each country.
        </p>
      </div>

      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <button 
            className="primary-button"
            onClick={() => {
              setEditingPolicy(null);
              setFormData({ country: '', probation_period_months: '', description: '' });
              setShowPolicyModal(true);
            }}
          >
            Add Policy
          </button>
        </div>
      </div>

      {/* Probation Policies Section */}
      {policies.length === 0 ? (
        <div style={{
          background: '#F6F4F0',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '3rem 2rem',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '1rem' }}></div>
          <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '0.5rem', color: 'var(--symbolica-black)' }}>
            No probation policies configured
          </h3>
          <p style={{ color: 'var(--symbolica-muted)', fontSize: '14px', marginBottom: '1.5rem' }}>
            Add probation policies for different countries to get started.
          </p>
        </div>
      ) : (
        <div style={{
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#F6F4F0',
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--symbolica-black)', margin: 0 }}>
              Probation Policies ({policies.length})
            </h3>
          </div>
          
          <div style={{ padding: '0' }}>
            {policies.map((policy, index) => (
              <div
                key={policy.id}
                style={{
                  padding: '1.5rem',
                  borderBottom: index < policies.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <h4 style={{ 
                      fontSize: '1rem', 
                      fontWeight: '600', 
                      color: 'var(--symbolica-black)', 
                      margin: 0 
                    }}>
                      {getCountryLabel(policy.country)}
                    </h4>
                    <span style={{
                      fontSize: '12px',
                      padding: '2px 8px',
                      borderRadius: '12px',
                      background: 'var(--symbolica-warm-border)',
                      color: 'var(--symbolica-muted)'
                    }}>
                      {policy.probation_period_months} months
                    </span>
                  </div>
                  
                  {policy.description && (
                    <div style={{ fontSize: '14px', color: 'var(--symbolica-muted)' }}>
                      {policy.description}
                    </div>
                  )}
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button 
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      backgroundColor: 'var(--symbolica-black)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                    onClick={() => handleEdit(policy)}
                  >
                    Edit
                  </button>
                  <button 
                    style={{
                      padding: '6px 12px',
                      fontSize: '12px',
                      backgroundColor: '#dc2626',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      fontWeight: '500'
                    }}
                    onClick={() => handleDelete(policy.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Policy Modal */}
      {showPolicyModal && (
        <Modal isOpen={showPolicyModal} onClose={() => setShowPolicyModal(false)}>
          <div style={{ padding: '1.5rem' }}>
            <h3 style={{ 
              fontSize: '1.25rem', 
              fontWeight: '600', 
              marginBottom: '1rem',
              color: 'var(--symbolica-black)'
            }}>
              {editingPolicy ? 'Edit' : 'Add'} Probation Policy
            </h3>
            
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--symbolica-black)',
                  marginBottom: '0.5rem'
                }}>
                  Country *
                </label>
                <select
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a country</option>
                  {COUNTRIES.map(country => (
                    <option key={country.value} value={country.value}>
                      {country.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--symbolica-black)',
                  marginBottom: '0.5rem'
                }}>
                  Probation Period (months) *
                </label>
                <input
                  type="number"
                  value={formData.probation_period_months}
                  onChange={(e) => setFormData({ ...formData, probation_period_months: e.target.value })}
                  min="1"
                  max="24"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white'
                  }}
                />
              </div>

              <div style={{ marginBottom: '2rem' }}>
                <label style={{ 
                  display: 'block', 
                  fontSize: '14px', 
                  fontWeight: '500', 
                  color: 'var(--symbolica-black)',
                  marginBottom: '0.5rem'
                }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  placeholder="Optional description of the probation policy"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    resize: 'vertical'
                  }}
                />
              </div>
              
              <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-end', 
                gap: '12px' 
              }}>
                <button 
                  type="button" 
                  className="secondary-button"
                  onClick={() => setShowPolicyModal(false)}
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="primary-button"
                  disabled={submitting}
                >
                  {submitting ? 'Saving...' : (editingPolicy ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </Modal>
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
}
